import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Col, Row, Button } from 'react-bootstrap';
import moment from 'moment-timezone';
import DeviceLocation from './DeviceLocation';
import { sendCommand } from '../../actions/socket';
import { unassignCancode } from '../../actions/hardware';
import { Modal } from '../widgets';
import { NOT_APPLICABLE, DISPLAY_DATETIME_FORMAT2 } from '../../constants';

const DeviceInfo = (props) => {
    const { deviceInfo, timezone, show, onHide, farmId, sensorInfo, actions } = props;

    const Unavailable = <span className="un-available">{NOT_APPLICABLE}</span>;
    let qStatus = Unavailable, qTime = Unavailable, qTimeLabel = "Up Time",
        gpsTime = Unavailable, canConnection = Unavailable;

    const [confirmModal, setConfirmModal] = useState({ restart: false, canCode: false });
    const [isUnassigning, setIsUnassigning] = useState(false);

    const handleConfirmModal = (type, show) => {
        setConfirmModal({ ...confirmModal, [type]: show });
    };

    const confirmRestart = () => {
        const { deviceId } = deviceInfo;
        actions.sendCommand({ deviceId, command: { command: "shutdown" } });
        handleConfirmModal("restart", false);
    };

    const confirmUnassignCancode = () => {
        const { deviceId, deviceName } = deviceInfo;

        setIsUnassigning(true);
        actions.unassignCancode({ deviceId, deviceName, farmId }, error => {
            if (!error) actions.sendCommand({ deviceId, command: { command: "shutdown" } });
            setIsUnassigning(false);
        });
        handleConfirmModal("canCode", false);
    };

    if (sensorInfo) {
        if (sensorInfo.qConnection.connected !== null) {
            if (sensorInfo.qConnection.connected) {
                qStatus = <span className="badge badge-pill badge-success badge-sm-info">Connected</span>;
                qTime = <span>{sensorInfo.qConnection.uptime} <i className="fa fa-arrow-up" style={{ fontSize: '12px', color: 'green' }} /></span>;
            }
            else if (!sensorInfo.qConnection.connected) {
                qStatus = <span className="badge badge-pill badge-danger badge-sm-info">Disconnected</span>;
                qTime = <span>{sensorInfo.qConnection.uptime} <i className="fa fa-arrow-down" style={{ fontSize: '12px', color: 'red' }} /></span>;
                qTimeLabel = "Down Time";
            }
        }

        if (sensorInfo.canConnection.state !== null) {
            if (sensorInfo.canConnection.state === 1)
                canConnection = <span className="badge badge-pill badge-danger badge-sm-info">Disconnected</span>;
            else if (sensorInfo.canConnection.state === 2)
                canConnection = <span className="badge badge-pill badge-warning badge-sm-info">Needs Attention</span>;
            else if (sensorInfo.canConnection.state === 3)
                canConnection = <span className="badge badge-pill badge-success badge-sm-info">Connected</span>;
        }

        if (sensorInfo.gps.ts)
            gpsTime = moment.utc(sensorInfo.gps.ts).tz(timezone).format(DISPLAY_DATETIME_FORMAT2);
    };

    return <>
        <Modal.Confirm
            className="modal-bordered"
            show={confirmModal.restart}
            onConfirm={confirmRestart}
            onHide={() => handleConfirmModal("restart", false)}
            iconClassName="fa fa-question"
            title={{ content: "Confirmation" }}
            content={`Do you want to restart ${deviceInfo.deviceName}?`}
            variant="info"
            confirmButton={{ variant: "info" }}
        />

        <Modal.Confirm
            variant="warning"
            className="modal-bordered"
            show={confirmModal.canCode}
            onConfirm={confirmUnassignCancode}
            onHide={() => handleConfirmModal("canCode", false)}
            iconClassName="fa fa-exclamation-triangle"
            title={{ content: "Warning" }}
            content={`Do you want to unassign CAN code from ${deviceInfo.deviceName}?`}
        />

        <Modal
            size="lg"
            show={show}
            onHide={onHide}
            className="modal-deviceinfo">
            <Modal.Header closeButton>
                <Modal.Title className="text-center h5">{deviceInfo.egName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col lg={6} md={6} sm={6} xs={6}>
                        <h5>Device Info</h5>
                        <ul>
                            <li><b>Name: </b>
                                {deviceInfo.deviceName ? deviceInfo.deviceName : Unavailable}
                            </li>
                            <li><b>Model: </b> {deviceInfo.deviceDetail.model ? deviceInfo.deviceDetail.model : Unavailable} </li>
                            <li><b>IMEI: </b>
                                {deviceInfo.deviceDetail.imei ? deviceInfo.deviceDetail.imei : Unavailable}
                            </li>
                            <li><b>UUID: </b>
                                {deviceInfo.deviceId ? deviceInfo.deviceId : Unavailable}
                            </li>
                            <li>
                                <b>CAN Code: </b>
                                {deviceInfo.deviceDetail.canCode ? deviceInfo.deviceDetail.canCode : Unavailable}
                                {deviceInfo.deviceDetail.canCode &&
                                    <Button
                                        title="Unassign CAN code"
                                        variant="danger"
                                        size="sm"
                                        className={`btn-xs ml-3 ${isUnassigning && 'disable'}`}
                                        onClick={() => handleConfirmModal("canCode", true)}
                                        disabled={isUnassigning}
                                    >
                                        {isUnassigning ? "Unassigning..." : "Unassign"}
                                    </Button>}
                            </li>
                            {deviceInfo.deviceDetail.networkName && <li><b>Network: </b>
                                {deviceInfo.deviceDetail.networkName ? deviceInfo.deviceDetail.networkName : Unavailable}
                            </li>}

                            <li><b>App Version: </b>
                                {deviceInfo.deviceDetail.appVersion ? deviceInfo.deviceDetail.appVersion : Unavailable}
                            </li>
                            <li><b>OS Version: </b>
                                {deviceInfo.deviceDetail.osVersion ? deviceInfo.deviceDetail.osVersion : Unavailable}
                            </li>
                        </ul>
                        <h5>Battery Info</h5>
                        <ul>
                            <li>
                                <b>Status: </b>
                                {sensorInfo && sensorInfo.battery.state ? (sensorInfo.battery.state === 1 ? 'Charging' : 'Discharging') : Unavailable}
                            </li>
                            <li><b>Level: </b>{sensorInfo && sensorInfo.battery.percent ? sensorInfo.battery.percent + "%" : Unavailable}</li>
                            <li><b>Temperature: </b>{sensorInfo && sensorInfo.battery.celsius ? `${sensorInfo.battery.celsius} °C` : Unavailable}</li>
                        </ul>
                        <h5>Link</h5>
                        <ul>
                            <li><b>Status: </b>{qStatus}</li>
                            <li><b>{qTimeLabel}: </b>{qTime}</li>
                        </ul>
                        <h5>CAN Interface</h5>
                        <ul>
                            <li><b>ID: </b>
                                {sensorInfo && sensorInfo.canInterfaceHealth.canId ?
                                    sensorInfo.canInterfaceHealth.canId : Unavailable}
                            </li>
                            <li><b>Software Version: </b>
                                {sensorInfo && sensorInfo.canInterfaceHealth.softwareVersion ?
                                    sensorInfo.canInterfaceHealth.softwareVersion : Unavailable}
                            </li>
                            <li><b>Status: </b>{canConnection}</li>
                        </ul>
                    </Col>
                    <Col lg={6} md={6} sm={6} xs={6} className="p-0">
                        <h5>Location Info</h5>
                        <ul>
                            <li><b>Latitude: </b>{sensorInfo && sensorInfo.gps.lat ? sensorInfo.gps.lat : Unavailable} </li>
                            <li><b>Longitude: </b>{sensorInfo && sensorInfo.gps.lng ? sensorInfo.gps.lng : Unavailable}</li>
                            <li><b>Time: </b>{gpsTime}</li>
                        </ul>
                        <DeviceLocation
                            location={sensorInfo && sensorInfo.gps}
                            icon={deviceInfo.img.ic}
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    title="Restart device"
                    variant="danger"
                    size="sm"
                    onClick={() => handleConfirmModal("restart", true)}>
                    Restart
                </Button>
            </Modal.Footer>
        </Modal>
    </>
}

const mapStateToProps = (state, { item }) => {
    const { hardwareId, egId } = item || {};
    const findHardware = state.hardware.deviceList.payload.filter(e => e.hardwareId === hardwareId);
    return {
        deviceInfo: (findHardware.length > 0) ? findHardware[0] : null,
        sensorInfo: state.hardware.sensorListById.payload[egId] ?
            state.hardware.sensorListById.payload[egId] : null,
        timezone: state.farm.currentFarm.timezone_identifier
    };
};

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({ sendCommand, unassignCancode }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(DeviceInfo);